import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import AdminLayout from '@/components/AdminLayout'
import { supabase } from '@/integrations/supabase/client'
import ResultSummary from '@/components/ResultSummary'
import { Button } from '@/components/ui/button'
import { ArrowLeft } from 'lucide-react'

const AdminResultDetail = () => {
  const { testId, assignmentId } = useParams()
  const navigate = useNavigate()
  const [resultData, setResultData] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchDetail = async () => {
      if (!assignmentId) return

      // Fetch for Single User
      const { data, error } = await supabase
        .from('employee_tests')
        .select(
          `
            id,
            created_at,
            test_id,
            started_at,
            completed_at,
            assignment_due_date,
            status,
            total_cheat_events,
            attempt_count,
            test:tests (
                categories,
                total_questions,
                time_limit
            ),
            employee:employee_id (
                id,
                personel,
                sicil,
                magaza
            ),
            employee_answers!employee_test_id (
                question_id,
                choice_id,
                is_correct,
                answered_at,
                question:question_id (
                    text,
                    category,
                    choices:choices (
                        id,
                        label,
                        text,
                        is_correct
                    )
                ),
                selected_choice:choice_id (
                    label,
                    text
                )
            ),
            cheat_events!employee_test_id (
                id,
                event_type,
                offset,
                verified,
                attempt_number
            )
        `
        )
        .eq('id', assignmentId) // Fetch specific assignment
        .single()

      if (error || !data) {
        console.error('Error fetching details', error)
        setLoading(false)
        return
      }

      const dataArray = [data]

      // Calculation Logic
      const base = dataArray.map((t) => {
        let categories: Record<string, { weight: number; no_questions: number }> = {}

        const raw = t.test?.categories
        if (typeof raw === 'string') {
          categories = JSON.parse(raw)
        } else if (raw && typeof raw === 'object') {
          categories = raw as Record<string, { weight: number; no_questions: number }>
        }

        const responses =
          t.employee_answers?.map((a: any) => {
            const qs = a.question!
            const allChoices = Array.isArray(qs.choices)
              ? qs.choices.map((choice: any) => ({
                  id: choice.id,
                  text: choice.text,
                  isCorrect: choice.is_correct,
                  isSelected: choice.id === a.choice_id,
                }))
              : []

            const correctText = allChoices.find((c: any) => c.isCorrect)?.text || null
            const selectedText = allChoices.find((c: any) => c.isSelected)?.text || ''

            return {
              questionId: a.question_id,
              category: qs.category,
              questionText: qs.text,
              choiceId: a.choice_id,
              selectedText: selectedText,
              correctText: correctText,
              isCorrect: a.is_correct,
              answeredAt: a.answered_at,
              allChoices: allChoices,
            }
          }) || []

        const totalQuestions = t.test?.total_questions || 0
        const timeLimit = t.test?.time_limit || 0
        const totalCorrect = responses.filter((r: any) => r.isCorrect).length
        const score = totalQuestions ? Math.round((totalCorrect / totalQuestions) * 100) : 0

        interface CatStat {
          correct: number
          total: number
          weight: number
          percent: number
        }
        const catStats: Record<string, CatStat> = {}

        for (const [catName, { weight, no_questions }] of Object.entries(categories)) {
          const resp = responses.filter((r: any) => r.category === catName)
          const correct = resp.filter((r: any) => r.isCorrect).length
          const percent = no_questions ? Math.round((correct / no_questions) * 100) : 0

          catStats[catName] = { correct, total: resp.length, weight, percent }
        }

        const weightedScore = Number(
          Object.values(catStats)
            .reduce((sum, { percent, weight }) => sum + (percent * weight) / 100, 0)
            .toFixed(2)
        )

        const cheatingEvents = (t.cheat_events || [])
          .filter((e: any) => e.event_type !== 'FACE_DETECTED')
          .map((e: any) => ({
            id: e.id,
            type: e.event_type,
            offset: e.offset,
            verified: e.verified,
            attempt_number: e.attempt_number ?? 1,
          }))

        const tabViolations = cheatingEvents.filter((e: any) => e.type === 'TAB_SWITCHING').length
        const cheatingDetected = cheatingEvents.length > 0

        return {
          testId: t.test_id,
          assignmentId: t.id,
          candidateId: t.employee?.id || '',
          candidateName: t.employee?.personel || 'Unknown',
          sicil: t.employee?.sicil || '',
          magaza: t.employee?.magaza || 'Unknown',
          timestamp: t.created_at,
          startedAt: t.started_at,
          completedAt: t.completed_at,
          assignmentDueDate: t.assignment_due_date,
          status: t.status,
          total_cheat_events: t.total_cheat_events,
          attempt_count: t.attempt_count || 1,
          responses,
          totalCorrect,
          totalQuestions,
          timeLimit,
          score,
          weightedScore,
          categories,
          videoUrl: '',
          cheatingSummary: {
            tabViolations,
            cheatingDetected,
            cheatingEvents,
          },
        }
      })

      setResultData(base[0])
      setLoading(false)
    }

    fetchDetail()
  }, [assignmentId])

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-10 text-center">Hesaplanıyor...</div>
      </AdminLayout>
    )
  }

  if (!resultData) return null

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(`/admin/results/${testId}`)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{resultData.candidateName}</h1>
            <p className="text-gray-500">Sınav Detayları ve Analizi</p>
          </div>
        </div>

        <ResultSummary result={resultData} />
      </div>
    </AdminLayout>
  )
}

export default AdminResultDetail
